import {
  APIFieldType,
  SurveyQuestionValidator,
  SurveyQuestionType,
  ArdoqId,
} from '@ardoq/api-types';
import { isArray, isNumber } from 'lodash';
import { pluralize } from '@ardoq/common-helpers';
import { type I18nLocale, t } from '@ardoq/i18n';
import { DateRange, FieldAnswerValue, ReferenceAnswerValue } from '../types';
import {
  isEmptyAnswer,
  isValidEmailAnswer,
  isValidDateRangeAnswer,
  isEmptyDateRangeAnswer,
} from '../utils/answerPredicates';

const getIncompletePriorityDateRangeQuestionWarning = (
  value: DateRange | undefined,
  i18nLocale: I18nLocale
) => {
  if (!value || (!value.endDate && !value.startDate)) return undefined;
  if (value.endDate)
    return t(
      i18nLocale,
      'surveysUi:dateRange.priorityMissingStart',
      'This is a priority question. The start date is missing.'
    );
  return t(
    i18nLocale,
    'surveysUi:dateRange.priorityMissingEnd',
    'This is a priority question. The end date is missing.'
  );
};

export type GetUserFeedbackReturn = {
  warning?: string;
  error?: string;
} | null;

type GetReferenceOrTagQuestionUserFeedbackArgs = {
  value: ReferenceAnswerValue[] | ArdoqId[] | undefined;
  rangeValidator?: SurveyQuestionValidator;
  questionType: SurveyQuestionType;
  noAccessReferences?: number;
  i18nLocale: I18nLocale;
};

export const getReferenceOrTagQuestionUserFeedback = ({
  value = [],
  rangeValidator,
  questionType,
  noAccessReferences = 0,
  i18nLocale,
}: GetReferenceOrTagQuestionUserFeedbackArgs): GetUserFeedbackReturn => {
  if (rangeValidator) {
    const { min, max } = rangeValidator;
    const nonUndefinedValue = isArray(value) ? value.length : 0;
    const totalResponses = nonUndefinedValue + noAccessReferences;
    if (min && min === max && min > 0 && totalResponses !== min) {
      const adjustedMax = max - noAccessReferences;
      return {
        warning: t(
          i18nLocale,
          'surveysUi:validation.specifyExactly',
          { count: adjustedMax, type: pluralize(questionType, adjustedMax) },
          'Please specify exactly {{count}} {{type}}'
        ),
      };
    } else if (min && totalResponses < min) {
      const adjustedMin = min - noAccessReferences;
      return {
        warning: t(
          i18nLocale,
          'surveysUi:validation.specifyAtLeast',
          { count: adjustedMin, type: pluralize(questionType, adjustedMin) },
          'Please specify at least {{count}} {{type}}.'
        ),
      };
    } else if (isNumber(max) && totalResponses > max) {
      const adjustedMax = max - noAccessReferences;
      return {
        error: t(
          i18nLocale,
          'surveysUi:validation.specifyAtMost',
          { count: adjustedMax, type: pluralize(questionType, adjustedMax) },
          'Please specify no more than {{count}} {{type}}.'
        ),
      };
    }
  }
  return null;
};

const getFieldNotFoundError = (i18nLocale: I18nLocale) => ({
  error: t(
    i18nLocale,
    'surveysUi:validation.fieldNotFound',
    'Field type not found. Was the field removed from the workspace?'
  ),
});

const getEmptyMandatoryQuestionError = (
  i18nLocale: I18nLocale,
  fieldLabel?: string
) => ({
  error: fieldLabel
    ? t(
        i18nLocale,
        'surveysUi:validation.mandatoryFieldWithLabel',
        { label: fieldLabel },
        '{{label}} must be filled in before submitting a response.'
      )
    : t(
        i18nLocale,
        'surveysUi:validation.mandatoryField',
        'Field must be filled in before submitting a response.'
      ),
});

type GetFieldOrAttributeQuestionUserFeedbackArgs = {
  isMandatory?: boolean;
  value: FieldAnswerValue | undefined;
  type?: APIFieldType;
  fieldLabel?: string;
  i18nLocale: I18nLocale;
};

export const getFieldOrAttributeQuestionUserFeedback = ({
  isMandatory = false,
  value,
  type,
  fieldLabel,
  i18nLocale,
}: GetFieldOrAttributeQuestionUserFeedbackArgs): GetUserFeedbackReturn => {
  if (type === undefined) {
    return getFieldNotFoundError(i18nLocale);
  }

  if (isMandatory && isEmptyAnswer(value)) {
    return getEmptyMandatoryQuestionError(i18nLocale, fieldLabel);
  }

  if (type === APIFieldType.EMAIL && !isValidEmailAnswer(value)) {
    return {
      error: t(
        i18nLocale,
        'surveysUi:validation.invalidEmail',
        'Invalid email address'
      ),
    };
  }

  return null;
};

type GetDateFieldQuestionUserFeedbackArgs = {
  isMandatory?: boolean;
  isRequired?: boolean;
  value: DateRange | undefined;
  type?: APIFieldType;
  fieldLabel?: string;
  i18nLocale: I18nLocale;
};

export const getDateFieldQuestionUserFeedback = ({
  isMandatory = false,
  isRequired = false,
  value,
  type,
  fieldLabel,
  i18nLocale,
}: GetDateFieldQuestionUserFeedbackArgs): GetUserFeedbackReturn => {
  if (type === undefined) {
    return getFieldNotFoundError(i18nLocale);
  }

  if (!isValidDateRangeAnswer(value)) {
    return {
      error: t(
        i18nLocale,
        'surveysUi:dateRange.startBeforeEnd',
        'Start date must be before end date.'
      ),
    };
  }

  if (isMandatory && isEmptyDateRangeAnswer(value)) {
    return getEmptyMandatoryQuestionError(i18nLocale, fieldLabel);
  }

  if (isRequired && isEmptyDateRangeAnswer(value)) {
    return {
      warning: getIncompletePriorityDateRangeQuestionWarning(value, i18nLocale),
    };
  }

  return null;
};
